import { Trans, useLingui } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import { useActivities } from '@/models/article';
import { ArticleWithRelations } from '@/models/request';

import { REPAIR_ISSUES } from '../ArticleActions/ReportRepairIssueDialog';

import { QualityControlMessage } from './shared/QualityControlMessage';
import { BaseMessage, BaseMessageDetailedContent } from './BaseMessage';

export const ChooseArticleServiceTaskMessage = ({ article }: { article: ArticleWithRelations }) => {
  const { i18n, t } = useLingui();

  const currentStepIndex = article.steps?.findIndex((step) => step.status === 'in-progress');
  const previousStep = currentStepIndex ? article.steps?.[currentStepIndex - 1] : null;

  const hasRepairIssueReportedOnPreviousStep =
    currentStepIndex &&
    previousStep &&
    previousStep.status === 'cancelled' &&
    previousStep.step === 'repair';
  const { data: { activities: exitRepairActivities } = { activities: [] } } = useActivities(
    {
      articleId: article.id,
      types: ['article_repair_issue_reported'],
    },
    { enabled: !!hasRepairIssueReportedOnPreviousStep }
  );
  const lastActivityExitRepair = exitRepairActivities[0];
  const issueDataExitRepair = REPAIR_ISSUES.find(
    ({ id }) => id === lastActivityExitRepair?.data.issue
  );
  const issueFormatedExitRepair = issueDataExitRepair ? i18n._(issueDataExitRepair.label) : null;
  const commentFormatedExitRepair = lastActivityExitRepair?.data?.comment
    ? `${t({ id: '_general.colon', message: ':' })} ${lastActivityExitRepair?.data?.comment}`
    : '';

  const isLastQualityControlKO = article.steps?.findLast(
    (step) => step.status === 'cancelled' && step.step === 'quality-control'
  );

  return hasRepairIssueReportedOnPreviousStep ? (
    <BaseMessage
      title={<Trans id="article.task.choose-article-service.title">Complete the diagnosis</Trans>}
      message={
        <Stack gap="0.25rem">
          <p>
            <Trans id="article.task.choose-article-service.message-repair-issue-reported-before">
              A repair issue has been reported and requires a new diagnosis for the item.
            </Trans>
          </p>
          <BaseMessageDetailedContent article={article}>
            <>
              <InlineText weight="medium">{issueFormatedExitRepair}</InlineText>
              {commentFormatedExitRepair}
            </>
          </BaseMessageDetailedContent>
        </Stack>
      }
      article={article}
    />
  ) : (
    <BaseMessage
      title={<Trans id="article.task.choose-article-service.title">Complete the diagnosis</Trans>}
      message={
        <Stack gap="0.25rem">
          <p>
            {isLastQualityControlKO ? (
              <Trans id="article.task.choose-article-service.message-after-quality-control-ko">
                An issue has been reported during quality control. Please review the item&apos;s
                details and needs, then select the appropriate service and actions.
              </Trans>
            ) : (
              <Trans id="article.task.choose-article-service.message">
                Please review the item's details and needs, then select the most suitable service
                and actions to provide.
              </Trans>
            )}
          </p>
          {isLastQualityControlKO && <QualityControlMessage article={article} />}
        </Stack>
      }
      article={article}
    />
  );
};
